/**
 * The seven screens, in order, and what each is called.
 *
 * One list, because three things have to agree about it and would drift if
 * each held its own: the progress bar counts it, the header reads its two
 * labels off it, and the review screen's Edit links jump back into it by id.
 *
 * Only the name of each step is written here. The number in front of it is
 * the step's position in whatever list this practitioner actually asks — see
 * `StepHeading`, which counts it — so "STEP 2 : Location" and the bar's "2/7"
 * are the same figure said twice rather than two numbers that can disagree.
 * A form with no location step numbers the one after it 2, which is what
 * somebody counting screens as they go would expect.
 */

import type { PreconsultationFields } from "./types/preconsultation.types";

export type PreconsultationStepId =
  | "basics"
  | "location"
  | "body"
  | "food"
  | "day"
  | "health"
  | "review";

export interface PreconsultationStep {
  id: PreconsultationStepId;
  /** "Basics" — the small line above the heading, after its number. */
  label: string;
  /** The heading itself, which is a question wherever it can be. */
  title: string;
}

export const PRECONSULTATION_STEPS: PreconsultationStep[] = [
  { id: "basics", label: "Basics", title: "Let’s get your basics" },
  { id: "location", label: "Location", title: "Where are you based?" },
  { id: "body", label: "Body", title: "Your body basics" },
  { id: "food", label: "Food", title: "How do you eat?" },
  { id: "day", label: "Your day", title: "How does your day run?" },
  { id: "health", label: "Health", title: "Anything we should know?" },
  { id: "review", label: "Review", title: "Does this look right?" },
];

/** Where a step sits, for the bar and for the Edit links. */
export const stepIndexOf = (
  id: PreconsultationStepId,
  steps: PreconsultationStep[] = PRECONSULTATION_STEPS
): number => steps.findIndex((step) => step.id === id);

/**
 * The steps this practitioner actually asks, out of what their `field_config`
 * says — see `PreconsultationFields`.
 *
 * A section nobody asks for is left out of the run rather than drawn empty:
 * the progress bar counts what is going to be asked, Continue steps over it,
 * and the review screen has no card for it. A step whose every question is
 * switched off would otherwise be a screen with a heading and nothing under
 * it, which reads as a form that failed to load.
 *
 * Three of the seven are not configurable and are always here. Basics and Body
 * carry every required answer on the form, and Review is not a question at
 * all. Food stays too: the allergy question on it has no flag of its own, and
 * an allergy is the one answer on this form that a plan can actually harm
 * somebody by not having.
 */
export const stepsAsked = (
  fields: PreconsultationFields
): PreconsultationStep[] =>
  PRECONSULTATION_STEPS.filter((step) => {
    switch (step.id) {
      case "location":
        return fields.location;
      case "day":
        return (
          fields.profession || fields.dailyRoutine || fields.lifestyleHabits
        );
      case "health":
        return fields.medicalConditions || fields.medications;
      default:
        return true;
    }
  });

/**
 * The lists the form falls back to where the backend serves no catalog.
 *
 * A form that could not ask what somebody eats because a lookup was missing
 * would be the wrong trade, so every one of these has a list of its own to
 * fall back on. They are `creatable` on the control, so none is a ceiling on
 * what somebody can say.
 *
 * The three habit questions are not here: their answers are the backend's own
 * choices rather than a catalog it might not have sent — see `HABIT_OPTIONS`.
 *
 * Neither is Goal, which used to lead this list. That field offers the
 * practice's own services and only those: a goal goes up as the `public_id` of
 * one of them, so a name off a list this app made up could only ever be sent
 * as nothing. See `goalRows`, where an empty Goal is argued for.
 */
export const PRECONSULTATION_FALLBACK_OPTIONS = {
  lifeStyles: ["Sedentary", "Semi active", "Active", "Very active"],
  foodPreferences: [
    "Vegetarian",
    "Eggetarian",
    "Non vegetarian",
    "Vegan",
    "Jain",
  ],
  cuisines: [
    "North Indian",
    "South Indian",
    "Gujarati",
    "Bengali",
    "Continental",
    "Chinese",
  ],
  medicalConditions: [
    "High cholesterol",
    "Diabetes",
    "Hypertension",
    "Thyroid",
    "PCOS",
  ],
  /**
   * The one fallback that is a worse answer than usual, and is still worth
   * having. An allergy goes up as a catalog id, so a name picked off this list
   * resolves against nothing and is sent as nothing — which means a form served
   * without the allergen catalog collects an allergy it cannot pass on. The
   * alternative is a field with an empty list under it, which tells the client
   * their dietitian is not asking. Named as the backend names them, so the two
   * lists agree wherever the real one does arrive.
   */
  foodAllergies: [
    "Milk Allergy",
    "Peanut Allergy",
    "Tree Nut Allergy",
    "Egg Allergy",
    "Soy Allergy",
    "Wheat Allergy",
    "Shellfish Allergy",
  ],
  professions: [
    "Software engineer",
    "Teacher",
    "Doctor",
    "Homemaker",
    "Student",
    "Business owner",
  ],
} as const;

/** One habit answer: the constant the backend stores, and what a screen reads
 *  it as. Shaped as the dropdowns' own option, so it can be handed straight to
 *  one — see `DropdownField`, which takes a label and a value that differ. */
export interface HabitOption {
  value: string;
  label: string;
}

/**
 * Travel, alcohol and smoking — the three questions whose answers are a fixed
 * scale rather than a name.
 *
 * These are not a fallback and not a catalog: they are the backend's own
 * `TravelFrequency`, `AlcoholFrequency` and `SmokingFrequency` choices, which
 * means the value is not this app's to invent. A form that sent
 * `Occasionally` where the column takes `OCCASIONALLY` would be refused, so
 * the constant is what the control holds and the label is only what it shows.
 *
 * Note the two are not interchangeable across the three: travel's middle
 * answer is `OCCASIONAL` and drink's is `OCCASIONALLY`, which is why each list
 * carries its own values rather than being folded into one table of words.
 */
export const HABIT_OPTIONS = {
  travel: [
    { value: "NONE", label: "Rarely/never travels" },
    { value: "OCCASIONAL", label: "Occasional travel" },
    { value: "FREQUENT", label: "Frequent travel" },
  ],
  alcohol: [
    { value: "NEVER", label: "Never" },
    { value: "OCCASIONALLY", label: "Occasionally" },
    { value: "WEEKLY", label: "Weekly" },
    { value: "DAILY", label: "Daily" },
  ],
  smoking: [
    { value: "NEVER", label: "Never" },
    { value: "OCCASIONALLY", label: "Occasionally" },
    { value: "WEEKLY", label: "Weekly" },
    { value: "DAILY", label: "Daily" },
  ],
} as const satisfies Record<string, readonly HabitOption[]>;

/** Which of the three a value belongs to. */
export type HabitField = keyof typeof HABIT_OPTIONS;

/** How many dishes either list takes. The design says so on the label, and
 *  the control enforces it — a list of thirty is not an answer anybody reads. */
export const MAX_DISHES = 5;

/** Every constant the three lists hold, against the label it reads as. The
 *  labels a value maps to are the same wherever it appears, so one flat table
 *  is enough to read any of them back. */
const HABIT_LABELS: Record<string, string> = Object.fromEntries(
  Object.values(HABIT_OPTIONS)
    .flat()
    .map((option) => [option.value, option.label])
);

/**
 * A stored habit constant, as a screen shows it — `OCCASIONAL` reads as
 * "Occasional travel".
 *
 * A value the lists have never heard of is written out rather than dropped:
 * these columns held free text before they held choices, and a record from
 * then still has to be readable. That fallback is `formatEnumLabel`'s rule —
 * `SOCIAL` as "Social" — spelled again here rather than imported, because a
 * shared control must not reach into the clients feature to draw itself.
 */
export const habitLabel = (value: string): string => {
  const held = (value ?? "").trim();
  if (!held) return "";
  const known = HABIT_LABELS[held.toUpperCase()];
  if (known) return known;
  const words = held.replace(/_/g, " ").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * The words these fields used to hold, against the choice that now means the
 * same thing.
 *
 * Records written before the backend fixed its choices hold "Rarely",
 * "Socially", `REGULAR` and the rest. They are only read here, on the way into
 * a form — nothing writes them again.
 *
 * "Regularly" becomes `WEEKLY` rather than `DAILY`: it was one step above
 * occasional on a three-point scale, and the smaller of the two readings is
 * the one that cannot overstate what somebody said about their drinking.
 */
const LEGACY_HABIT_VALUES: Record<HabitField, Record<string, string>> = {
  // Travel had no "none" of its own — never and rarely were both the bottom
  // of the scale, and both are what `NONE` now says.
  travel: {
    NEVER: "NONE",
    RARELY: "NONE",
    SOMETIMES: "OCCASIONAL",
    OFTEN: "FREQUENT",
  },
  alcohol: {
    OCCASIONAL: "OCCASIONALLY",
    SOCIAL: "OCCASIONALLY",
    SOCIALLY: "OCCASIONALLY",
    REGULAR: "WEEKLY",
    REGULARLY: "WEEKLY",
  },
  smoking: {
    OCCASIONAL: "OCCASIONALLY",
    SOCIAL: "OCCASIONALLY",
    SOCIALLY: "OCCASIONALLY",
    REGULAR: "WEEKLY",
    REGULARLY: "WEEKLY",
  },
};

/**
 * What a form should open one of these fields on, given whatever the record
 * holds.
 *
 * Empty for a value neither the list nor the table above can account for. That
 * reads as unanswered, which is the safe end of it: an unanswered habit is
 * left out of the payload entirely, so the record keeps whatever it had
 * instead of the form sending back a word the column would now refuse.
 */
export const habitValue = (field: HabitField, stored: string): string => {
  const held = (stored ?? "").trim();
  if (!held) return "";
  const token = held.toUpperCase().replace(/\s+/g, "_");
  const known = HABIT_OPTIONS[field].some((option) => option.value === token);
  return known ? token : (LEGACY_HABIT_VALUES[field][token] ?? "");
};
